<template>
	<div class="g-color-picker" ref="rootRef">
		<div  ref="triggerRef" class="g-color-picker__trigger" @mousedown.stop="onTriggerMouseDown" @click.stop="toggle">
			<span
				v-if="!hideTrigger"
				class="g-color-picker__swatch"
				:class="{ 'is-transparent': !modelValue || modelValue === 'transparent' }"
				:style="swatchStyle"
			></span>
		</div>

		<teleport to="body">
			<div
				v-if="visible"
				ref="dropdownRef"
				class="g-color-picker__dropdown"
				:style="dropdownStyle"
				@click.stop
				@mousedown.stop
				@wheel.stop
			>
			<div class="g-color-picker__panel">
				<!-- 顶部：左（预设+最近）+ 右（饱和度/色相/HEX） -->
				<div class="g-color-picker__top">
					<!-- 左侧：预设颜色 + 最近使用 -->
					<div class="g-color-picker__left">
						<div class="g-color-picker__section">
							<div class="g-color-picker__section-label">预设颜色</div>
							<div class="g-color-picker__color-list">
								<span
									v-for="(color, i) in presetColors"
									:key="`p-${i}`"
									class="g-color-picker__color-swatch"
									:class="{ 'is-active': isColorActive(color) }"
									:style="{ backgroundColor: color }"
									@mousedown.stop.prevent="pickColor(color)"
								></span>
							</div>
						</div>
						<div v-if="recentColors.length > 0" class="g-color-picker__section">
							<div class="g-color-picker__section-label">最近使用</div>
							<div class="g-color-picker__color-list">
								<span
									v-for="(color, i) in recentColors"
									:key="`r-${i}`"
									class="g-color-picker__color-swatch"
									:class="{ 'is-active': isColorActive(color) }"
									:style="{ backgroundColor: color }"
									@mousedown.stop.prevent="pickColor(color)"
								></span>
							</div>
						</div>
					</div>

					<!-- 右侧：饱和度 + 色相 + HEX -->
					<div class="g-color-picker__right">
						<div
							ref="saturationRef"
							class="g-color-picker__saturation"
							:style="{ backgroundColor: hueColor }"
							@mousedown.prevent="onSaturationMouseDown"
						>
							<div class="g-color-picker__saturation-white"></div>
							<div class="g-color-picker__saturation-black"></div>
							<div
								class="g-color-picker__saturation-cursor"
								:style="{ left: saturationPercent + '%', top: brightnessPercent + '%' }"
							></div>
						</div>
						<div class="g-color-picker__hue-row">
							<div
								ref="hueRef"
								class="g-color-picker__hue"
								@mousedown.prevent="onHueMouseDown"
							>
								<div
									class="g-color-picker__hue-cursor"
									:style="{ left: (hue / 360) * 100 + '%' }"
								></div>
							</div>
							<div class="g-color-picker__hex-input-wrap">
								<span class="g-color-picker__hash">#</span>
								<input
									type="text"
									class="g-color-picker__hex-input"
									:value="hexInput"
									@change="onHexInputChange"
									@blur="onHexInputBlur"
									@input="onHexInput"
									maxlength="7"
									@mousedown.stop
								/>
							</div>
						</div>
					</div>
				</div>

				<!-- 底部：取消 + 确认 -->
				<div class="g-color-picker__footer">
					<button
						type="button"
						class="g-color-picker__btn g-color-picker__btn--cancel"
						@mousedown.stop.prevent="cancel"
					>取消</button>
					<button
						type="button"
						class="g-color-picker__btn g-color-picker__btn--confirm"
						@mousedown.stop.prevent="confirm"
					>确认</button>
				</div>
			</div>
		</div>
	</teleport>
</div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';

const PRESET_COLORS = [
	'#000000', '#323B49', '#616D7B', '#969EA9', '#C7CCD2', '#E0E4E8', '#FFFFFF',
	'#5B0A35', '#D52315', '#F43336', '#E76300', '#FF9F00', '#01A25C', '#008073',
	'#00A8CC', '#0060F3', '#543BEF', '#7B26D9', '#AB1BD2', '#EA007F', '#F93162',
	'#284B81', '#2A73A0', '#5088AB', '#6181BE', '#7F6FAE', '#D56978', '#DC7F3A',
	'#0D1C3D', '#00505B', '#03418E', '#382B83', '#621585', '#990745', '#A73F00',
];

const MAX_RECENT_COLORS = 12;

export default {
	name: 'ColorPicker',
	props: {
		modelValue: {
			type: String,
			default: '#000000',
		},
		hideTrigger: {
			type: Boolean,
			default: false,
		},
		swatchWidth: {
			type: [Number, String],
			default: undefined,
		},
		swatchHeight: {
			type: [Number, String],
			default: undefined,
		},
	},
	emits: ['update:modelValue', 'change', 'cancel'],
	setup(props, { emit }) {
		const visible = ref(false);
		const rootRef = ref(null);
		const triggerRef = ref(null);
		const dropdownRef = ref(null);
		const saturationRef = ref(null);
		const hueRef = ref(null);

		const hue = ref(0);
		const saturation = ref(100);
		const brightness = ref(100);
		const hexInput = ref('');

		const recentColors = ref([]);

		const presetColors = PRESET_COLORS;

		// 下拉面板定位（基于触发元素 bounding rect）
		const dropdownPosition = ref({ top: 0, left: 0, placement: 'bottom' });

		const dropdownStyle = computed(() => {
			const { top, left, placement } = dropdownPosition.value;
			return {
				top: `${top}px`,
				left: `${left}px`,
				transformOrigin: placement === 'top' ? 'center bottom' : 'center top',
			};
		});

		const hueColor = computed(() => {
			return `hsl(${hue.value}, 100%, 50%)`;
		});

		const saturationPercent = computed(() => saturation.value);
		const brightnessPercent = computed(() => 100 - brightness.value);

		const swatchStyle = computed(() => {
			const color = props.modelValue;
			const style = {};
			if (!color || color === 'transparent') {
			} else {
				style.backgroundColor = color;
			}
			if (props.swatchWidth !== undefined) {
				console.log(props.swatchWidth)
				style.width = typeof props.swatchWidth === 'number' ? props.swatchWidth + 'px' : props.swatchWidth;
			}
			if (props.swatchHeight !== undefined) {
				console.log(props.swatchHeight)
				style.height = typeof props.swatchHeight === 'number' ? props.swatchHeight + 'px' : props.swatchHeight;
			}
			console.log(style)
			return style;
		});

		function hexToHsv(hex) {
			const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
			if (!result) return { h: 0, s: 0, v: 100 };

			let r = parseInt(result[1], 16) / 255;
			let g = parseInt(result[2], 16) / 255;
			let b = parseInt(result[3], 16) / 255;

			const max = Math.max(r, g, b);
			const min = Math.min(r, g, b);
			const d = max - min;

			let h = 0;
			const s = max === 0 ? 0 : (d / max) * 100;
			const v = max * 100;

			if (d !== 0) {
				switch (max) {
					case r:
						h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
						break;
					case g:
						h = ((b - r) / d + 2) * 60;
						break;
					case b:
						h = ((r - g) / d + 4) * 60;
						break;
				}
			}

			return { h, s, v };
		}

		function hsvToHex(h, s, v) {
			const s1 = s / 100;
			const v1 = v / 100;
			const i = Math.floor(h / 60) % 6;
			const f = h / 60 - Math.floor(h / 60);
			const p = v1 * (1 - s1);
			const q = v1 * (1 - f * s1);
			const t = v1 * (1 - (1 - f) * s1);

			let r, g, b;
			switch (i) {
				case 0: r = v1; g = t; b = p; break;
				case 1: r = q; g = v1; b = p; break;
				case 2: r = p; g = v1; b = t; break;
				case 3: r = p; g = q; b = v1; break;
				case 4: r = t; g = p; b = v1; break;
				case 5: r = v1; g = p; b = q; break;
			}

			const toHex = (n) => {
				const hex = Math.round(n * 255).toString(16);
				return hex.length === 1 ? '0' + hex : hex;
			};

			return '#' + toHex(r) + toHex(g) + toHex(b);
		}

		function setHsvFromColor(color) {
			if (!color || color === 'transparent') {
				hue.value = 0;
				saturation.value = 0;
				brightness.value = 100;
				hexInput.value = '#000000';
				return;
			}
			const hsv = hexToHsv(color);
			hue.value = hsv.h;
			saturation.value = hsv.s;
			brightness.value = hsv.v;
			hexInput.value = color;
		}

		function saveToRecent(color) {
			if (!color || color === 'transparent') return;
			const existing = recentColors.value.indexOf(color);
			if (existing !== -1) {
				recentColors.value.splice(existing, 1);
			}
			recentColors.value.unshift(color);
			if (recentColors.value.length > MAX_RECENT_COLORS) {
				recentColors.value.pop();
			}
			try {
				localStorage.setItem('g-color-picker-recent', JSON.stringify(recentColors.value));
			} catch (e) {
				// ignore
			}
		}

		function loadRecentColors() {
			try {
				const saved = localStorage.getItem('g-color-picker-recent');
				if (saved) {
					recentColors.value = JSON.parse(saved);
				}
			} catch (e) {
				// ignore
			}
		}

		function toggle() {
			if (!visible.value) {
				openUid.value = pickerUid;
				setHsvFromColor(props.modelValue);
				updateDropdownPosition();
			}
			visible.value = !visible.value;
		}

		function onTriggerMouseDown(event) {
			event.preventDefault();
			event.stopPropagation();
		}

		function updateSaturation(clientX, clientY) {
			if (!saturationRef.value) return;
			const rect = saturationRef.value.getBoundingClientRect();
			const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
			const y = Math.max(0, Math.min(clientY - rect.top, rect.height));
			saturation.value = (x / rect.width) * 100;
			brightness.value = 100 - (y / rect.height) * 100;
			hexInput.value = hsvToHex(hue.value, saturation.value, brightness.value);
		}

		function updateHue(clientX) {
			if (!hueRef.value) return;
			const rect = hueRef.value.getBoundingClientRect();
			const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
			hue.value = (x / rect.width) * 360;
			hexInput.value = hsvToHex(hue.value, saturation.value, brightness.value);
		}

		let isDraggingSaturation = false;
		let isDraggingHue = false;

		function onSaturationMouseDown(e) {
			isDraggingSaturation = true;
			updateSaturation(e.clientX, e.clientY);
		}

		function onHueMouseDown(e) {
			isDraggingHue = true;
			updateHue(e.clientX);
		}

		function onMouseMove(e) {
			if (isDraggingSaturation) {
				updateSaturation(e.clientX, e.clientY);
			}
			if (isDraggingHue) {
				updateHue(e.clientX);
			}
			if (isDraggingSaturation || isDraggingHue) {
				const hex = hexInput.value.trim();
				if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
					emit('change', hex);
				}
			}
		}

		function onMouseUp() {
			isDraggingSaturation = false;
			isDraggingHue = false;
		}

		// 仅同步输入框本地值，不修改 hue/sat/bright 避免回路
		function onHexInput(e) {
			hexInput.value = e.target.value;
		}

		function onHexInputChange() {
			const val = hexInput.value.trim();
			if (/^#[0-9a-fA-F]{6}$/.test(val)) {
				const hsv = hexToHsv(val);
				hue.value = hsv.h;
				saturation.value = hsv.s;
				brightness.value = hsv.v;
				hexInput.value = val;
			}
		}

		function onHexInputBlur() {
			if (!/^#[0-9a-fA-F]{6}$/.test(hexInput.value.trim())) {
				hexInput.value = props.modelValue || '#000000';
			}
		}

		function pickColor(color) {
			setHsvFromColor(color);
			emit('change', color);
		}

		function normalizeHexColor(color) {
			if (!color) return '';
			if (color === 'transparent') return 'transparent';
			let c = String(color).trim();
			if (!c.startsWith('#')) c = '#' + c;
			if (/^#[0-9a-fA-F]{3}$/.test(c)) {
				c = '#' + c[1] + c[1] + c[2] + c[2] + c[3] + c[3];
			}
			return c.toLowerCase();
		}

		function isColorActive(color) {
			// 优先以当前正在编辑/预览的 hex 值作为选中依据，确保点击预设/最近色块时即可看到选中态，
			// 不必等到点击"确认"才更新 modelValue。
			const activeColor = (visible.value ? hexInput.value : props.modelValue) || props.modelValue;
			const a = normalizeHexColor(activeColor);
			const b = normalizeHexColor(color);
			return a && b && a === b;
		}

		function confirm() {
			const hex = hexInput.value.trim();
			if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
				emit('update:modelValue', hex);
				emit('change', hex);
				saveToRecent(hex);
			}
			visible.value = false;
			if (openUid.value === pickerUid) {
				openUid.value = null;
			}
		}

		function cancel() {
			visible.value = false;
			if (openUid.value === pickerUid) {
				openUid.value = null;
			}
			emit('cancel');
		}

		// 共享 openUid 控制同时只有一个 ColorPicker 处于打开状态
		const openUid = window.__gColorPickerOpenUid || (window.__gColorPickerOpenUid = ref(null));
		const pickerUid = Symbol('ColorPicker');

		const DROPDOWN_WIDTH = 360;
		const DROPDOWN_GAP = 2;
		const DROPDOWN_HEIGHT = 280;

		function updateDropdownPosition() {
			if (!triggerRef.value) {
				dropdownPosition.value = { top: 0, left: 0, placement: 'bottom' };
				return;
			}
			const rect = triggerRef.value.getBoundingClientRect();
			const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
			const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

			let left = rect.left + rect.width / 2 - DROPDOWN_WIDTH / 2;
			const minLeft = 8;
			const maxLeft = viewportWidth - DROPDOWN_WIDTH - 8;
			if (left < minLeft) left = minLeft;
			if (left > maxLeft) left = Math.max(minLeft, maxLeft);

			let top;
			let placement = 'bottom';
			const spaceBelow = viewportHeight - rect.bottom;
			const spaceAbove = rect.top;
			if (spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow) {
				top = rect.top - DROPDOWN_GAP - DROPDOWN_HEIGHT;
				placement = 'top';
			} else {
				top = rect.bottom + DROPDOWN_GAP;
				placement = 'bottom';
			}

			dropdownPosition.value = { top, left, placement };
		}

		function onWindowChange() {
			if (visible.value) {
				updateDropdownPosition();
			}
		}

		function handleDocumentMouseDown(event) {
			if (!visible.value) return;
			const target = event.target;
			const rootEl = rootRef.value;
			if (!rootEl) return;
			const dropdownEl = dropdownRef.value;
			const triggerEl = triggerRef.value;
			if (dropdownEl && dropdownEl.contains(target)) return;
			if (triggerEl && triggerEl.contains(target)) return;
			visible.value = false;
			if (openUid.value === pickerUid) {
				openUid.value = null;
			}
		}

		watch(openUid, (uid) => {
			if (uid !== pickerUid && visible.value) {
				visible.value = false;
			}
		});

		// 监听外部 modelValue 变化时，仅在弹窗关闭状态下同步内部 HSV，避免拖动时的循环
		watch(() => props.modelValue, (val) => {
			if (!visible.value) {
				setHsvFromColor(val);
			}
		});

		onMounted(() => {
			setHsvFromColor(props.modelValue);
			loadRecentColors();
			document.addEventListener('mousemove', onMouseMove);
			document.addEventListener('mouseup', onMouseUp);
			document.addEventListener('mousedown', handleDocumentMouseDown, true);
			window.addEventListener('resize', onWindowChange);
			window.addEventListener('scroll', onWindowChange, true);
		});

		onBeforeUnmount(() => {
			document.removeEventListener('mousemove', onMouseMove);
			document.removeEventListener('mouseup', onMouseUp);
			document.removeEventListener('mousedown', handleDocumentMouseDown, true);
			window.removeEventListener('resize', onWindowChange);
			window.removeEventListener('scroll', onWindowChange, true);
			if (openUid.value === pickerUid) {
				openUid.value = null;
			}
		});

		return {
			visible,
			rootRef,
			triggerRef,
			dropdownRef,
			saturationRef,
			hueRef,
			hue,
			saturation,
			brightness,
			hexInput,
			presetColors,
			recentColors,
			dropdownStyle,
			hueColor,
			saturationPercent,
			brightnessPercent,
			swatchStyle,
			toggle,
			onTriggerMouseDown,
			onSaturationMouseDown,
			onHueMouseDown,
			onHexInput,
			onHexInputChange,
			onHexInputBlur,
			pickColor,
			isColorActive,
			confirm,
			cancel,
		};
	},
};
</script>

<style scoped lang="less">
.g-color-picker {
	position: relative;
	display: inline-block;

	&__trigger {
		cursor: pointer;
	}

	&__swatch {
		display: block;
		width: 18px;
		height: 18px;
		border: 1px solid #b0b0b0;
		border-radius: 3px;
		box-sizing: border-box;

		&.is-transparent {
			background-image: linear-gradient(45deg, #ccc 25%, transparent 25%),
				linear-gradient(-45deg, #ccc 25%, transparent 25%),
				linear-gradient(45deg, transparent 75%, #ccc 75%),
				linear-gradient(-45deg, transparent 75%, #ccc 75%);
			background-size: 8px 8px;
			background-position: 0 0, 0 4px, 4px -4px, -4px 0px;
		}
	}

	&__dropdown {
		position: fixed;
		z-index: 99999;
		background: #fff;
		border: 1px solid #eceef2;
		border-radius: 6px;
		box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
		padding: 12px;
		width: 360px;
		box-sizing: border-box;
	}

	&__panel {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	&__top {
		display: flex;
		gap: 12px;
		align-items: stretch;
	}

	&__left {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	&__right {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	&__section {
		&-label {
			font-size: 11px;
			color: #999;
			margin-bottom: 6px;
		}
	}

	&__saturation {
		position: relative;
		width: 100%;
		height: 120px;
		border-radius: 4px;
		cursor: crosshair;
		overflow: hidden;

		&-white {
			position: absolute;
			inset: 0;
			background: linear-gradient(to right, #fff, transparent);
		}

		&-black {
			position: absolute;
			inset: 0;
			background: linear-gradient(to top, #000, transparent);
		}

		&-cursor {
			position: absolute;
			width: 12px;
			height: 12px;
			border: 2px solid #fff;
			border-radius: 50%;
			box-shadow: 0 0 3px rgba(0, 0, 0, 0.4), inset 0 0 3px rgba(0, 0, 0, 0.4);
			transform: translate(-50%, -50%);
			pointer-events: none;
		}
	}

	&__hue-row {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	&__hue {
		position: relative;
		flex: 1;
		height: 12px;
		border-radius: 6px;
		background: linear-gradient(
			to right,
			#ff0000 0%,
			#ffff00 17%,
			#00ff00 33%,
			#00ffff 50%,
			#0000ff 67%,
			#ff00ff 83%,
			#ff0000 100%
		);
		cursor: pointer;

		&-cursor {
			position: absolute;
			top: 50%;
			width: 14px;
			height: 14px;
			background: #fff;
			border: 1px solid #999;
			border-radius: 14px;
			box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
			transform: translate(-50%, -50%);
			pointer-events: none;
		}
	}

	&__hex-input-wrap {
		display: flex;
		align-items: center;
		height: 24px;
		padding: 0 6px;
		border: 1px solid #e8e8e8;
		border-radius: 4px;
		box-sizing: border-box;
		width: 86px;

		&:focus-within {
			border-color: #285af9;
		}
	}

	&__hash {
		font-size: 12px;
		color: #999;
		margin-right: 2px;
	}

	&__hex-input {
		flex: 1;
		min-width: 0;
		border: none;
		outline: none;
		background: transparent;
		font-size: 12px;
		color: #333;
		padding: 0;
	}

	&__color-list {
		display: flex;
		flex-wrap: wrap;
		gap: 4px;
		max-height: 112px;
	}

	&__color-swatch {
		width: 19px;
		height: 19px;
		border-radius: 3px;
		border: 1px solid rgba(0, 0, 0, 0.1);
		cursor: pointer;
		flex-shrink: 0;
		box-sizing: border-box;

		&:hover {
			border-color: #1890ff;
		}

		&.is-active {
			border-color: #1890ff;
			box-shadow: 0 0 0 1px #1890ff;
		}
	}

	&__footer {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
		padding-top: 4px;
	}

	&__btn {
		height: 28px;
		padding: 0 14px;
		border-radius: 4px;
		font-size: 12px;
		cursor: pointer;
		border: 1px solid transparent;
		transition: background 0.15s, border 0.15s, color 0.15s;

		&--cancel {
			background: #fff;
			border-color: #e0e0e0;
			color: #555;

			&:hover {
				border-color: #285af9;
				color: #285af9;
			}
		}

		&--confirm {
			background: #285af9;
			border-color: #285af9;
			color: #fff;

			&:hover {
				background: #1f4ad8;
				border-color: #1f4ad8;
			}
		}
	}
}
</style>
